'use client';

import classNames from 'classnames';
import countBy from 'lodash/countBy';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import orderBy from 'lodash/orderBy';
import range from 'lodash/range';
import sumBy from 'lodash/sumBy';
import { Info } from 'lucide-react';
import { createContext, FC, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LiveRoundStatus } from '@spikemark/live';
import { CutSetup, ElasticCourseData, ElasticLeaderboardWithAveragePoints, ElasticRoundAnalyticsWithAveragePoints, ElasticTournament, TiebreakData, Tournament } from '@spikemark/rest-api';
import { useFetch, useIsMinimumTablet } from '@spikemark/shared-hooks';
import { NoTournamentData } from '@spikemark/ui/components/placeholder/no-tournament-data';
import { FilterSelector } from '@spikemark/ui/components/query/filter-selector';
import { HoleScorecard } from '@spikemark/ui/components/scorecard/hole-scorecard';
import { ScorecardDialogProvider, useScorecardDialogContext } from '@spikemark/ui/components/scorecard/scorecard-dialog-provider';
import { LeaderboardDisplayMode, LeaderboardType, TieWinner } from '@spikemark/ui/types/leaderboard';
import { createCourseInfoMap } from '@spikemark/ui/utils/scoring';
import { getRoundStatus } from '@spikemark/ui/utils/tournament';
import { useSearchParams } from 'next/navigation';
import { useCut } from '../hooks/use-leaderboard-cut';
import { Table } from '../table';
import { TableColumn, TableProps, TableRowExpandableComponentProps, TableRowOptions } from '../types';
import { createStrokeplayColumns } from './columns/strokeplay-columns';
const CUMULATIVE_DROP_SCORE_COLUMN_ID = 'c_dropScore';
export type StrokeplayLeaderboardProps = {
  cutSetup?: CutSetup;
  type: LeaderboardType;
  mode?: LeaderboardDisplayMode;
  tournament: ElasticTournament;
  playerLeaderboard: ElasticLeaderboardWithAveragePoints | null;
  teamLeaderboard?: ElasticLeaderboardWithAveragePoints | null;
  numRounds: number;
  rankingsEnabled?: boolean;
  isAdmin?: boolean;
  isLive?: boolean;
  pollingInterval: number;
  winnerCount?: number;
  winner?: TieWinner;
  onWinnerSelect?: (value: TieWinner) => void;
  cutLineWinner?: TieWinner;
  selectCutLineWinner?: (value: TieWinner) => void;
  allRoundsCompleted?: boolean;
  playerRankingsDisplayCutoff?: number;
  teamRankingsDisplayCutoff?: number;
  dynRoundStatuses?: Tournament['roundStatuses'];
  scoringPlayers?: Tournament['numTeamScoring'];
  isUpdatedAfterPublish?: boolean;
  cutLineTiesConfig?: {
    isCutLineTie: boolean;
    cutLineRank: number;
  };
};
export const StrokeplayLeaderboard = memo(({
  cutSetup,
  type,
  playerLeaderboard,
  teamLeaderboard,
  numRounds,
  rankingsEnabled,
  isAdmin,
  isLive,
  tournament,
  winnerCount,
  winner,
  pollingInterval,
  onWinnerSelect,
  cutLineWinner,
  selectCutLineWinner,
  allRoundsCompleted,
  playerRankingsDisplayCutoff,
  teamRankingsDisplayCutoff,
  dynRoundStatuses,
  cutLineTiesConfig,
  scoringPlayers,
  isUpdatedAfterPublish
}: StrokeplayLeaderboardProps) => {
  const searchParams = useSearchParams();
  const displayMode: LeaderboardDisplayMode = (searchParams?.get('displayMode') || 'score') as LeaderboardDisplayMode;
  const isMinimumTablet = useIsMinimumTablet();
  const mobile = !isMinimumTablet;
  const queryBaseUrl = `/api/tournaments/${tournament.tournamentId}/leaderboards`;
  const {
    data: livePlayerResult
  } = useFetch<ElasticLeaderboardWithAveragePoints>(`${queryBaseUrl}/player`, 'GET', {
    reset: false,
    pollingInterval: isLive ? pollingInterval : undefined,
    skip: !isLive
  });
  const {
    data: liveTeamResult
  } = useFetch<ElasticLeaderboardWithAveragePoints>(`${queryBaseUrl}/team`, 'GET', {
    reset: false,
    pollingInterval: isLive ? pollingInterval : undefined,
    skip: !isLive || type === 'player'
  });
  const playerLeaderboardData = livePlayerResult ?? playerLeaderboard;
  const teamLeaderboardData = liveTeamResult ?? teamLeaderboard;
  const hasTeamTies = useMemo(() => {
    const rankCounts = countBy(teamLeaderboardData?.results, 'currentRank');
    return Object.values(rankCounts).some(count => count > 1);
  }, [teamLeaderboardData]);
  const {
    data: tieData,
    isLoading
  } = useFetch<TiebreakData[]>(`/api/tournaments/${tournament.tournamentId}/tiebreak`, 'GET', {
    skip: isLive || !allRoundsCompleted
  });
  const courseInfoMap = createCourseInfoMap(playerLeaderboardData?.courses);
  const isMultiCourse = Object.keys(courseInfoMap).length > numRounds;
  const {
    leaderboardData
  } = useMemo(() => {
    const leaderboardData = type === 'player' ? playerLeaderboardData : teamLeaderboardData;
    if (type === 'team') {
      let updatedResult: ElasticRoundAnalyticsWithAveragePoints[] | undefined = [];
      if (tieData?.length && tieData.some(tie => tie.tiebreakPosition)) {
        updatedResult = orderBy(leaderboardData?.results?.map(x => {
          const tiebreakData = tieData.find(y => {
            return y.entityId === x.schoolId;
          });
          // Reorder the results base on currentRank + tiebreak, offset by number of teams/players that are tied
          return {
            ...x,
            currentRank: tiebreakData?.tiebreakPosition || x.currentRank!
          };
        }) ?? [], ['currentRank'], ['asc']);
      } else if (hasTeamTies) {
        const groupedByTeam = groupBy(playerLeaderboardData?.results, datum => datum.schoolId);
        const dropScores = Object.fromEntries(Object.entries(groupedByTeam).map(([schoolId, players]) => {
          const scores = players.map(p => p.scores);
          const maxRounds = Math.max(...scores.map(s => s?.length || 0));
          const roundDropScores = Array.from({
            length: maxRounds
          }, (_, roundIndex) => {
            const sortedPlayersByRound = orderBy(players, [p => p.scores?.[roundIndex] ?? 0], ['asc']);
            const droppedPlayers = sortedPlayersByRound.slice(scoringPlayers ?? 5);
            return sumBy(droppedPlayers, p => p.scores?.[roundIndex] ?? 0);
          });
          const totalDropScore = sumBy(roundDropScores, score => score);
          return [schoolId, totalDropScore];
        }));
        updatedResult = leaderboardData?.results?.map(result => {
          return {
            ...result,
            [CUMULATIVE_DROP_SCORE_COLUMN_ID]: dropScores[result.schoolId] ?? 0
          };
        });
      } else {
        updatedResult = leaderboardData?.results;
      }
      return {
        leaderboardData: {
          ...leaderboardData,
          results: updatedResult
        }
      };
    }
    return {
      leaderboardData
    };
  }, [type, playerLeaderboardData, teamLeaderboardData, tieData, isLoading]);
  const {
    isCut,
    isFirstCutRow,
    cutStatus
  } = useCut({
    leaderboardData: leaderboardData?.results,
    cutSetup,
    type
  });
  const {
    currentRoundId,
    roundStatuses
  } = useMemo(() => {
    let currentRoundId = 1;
    let roundStatuses: Record<number, LiveRoundStatus> = {};
    range(1, numRounds + 1).map(roundId => {
      roundStatuses[roundId] = getRoundStatus(tournament, roundId) === 'ACTIVE' ? 'played' : 'cancelled';
    });
    if (leaderboardData?.results) {
      for (const result of leaderboardData.results) {
        if (result.rounds) {
          for (const round of result.rounds) {
            const roundStatus = roundStatuses[round.roundId];
            if (roundStatus !== 'cancelled' && round.thru !== '0') {
              currentRoundId = Math.max(currentRoundId, round.roundId ?? 0);
            }
          }
        }
      }
    }
    return {
      currentRoundId: String(currentRoundId),
      roundStatuses
    };
  }, [leaderboardData, tournament]);
  const {
    dynamicCutLinesConfig
  } = useMemo(() => {
    const tournamentCutlines = dynRoundStatuses?.map(round => ({
      roundId: round['roundId' as keyof typeof round] ?? 0,
      cutLine: Number(round?.['cutLine' as keyof typeof round] ?? 0)
    }));
    let dynamicCutLinesConfig: {
      rounds: Record<string, number>;
      isLive: boolean;
    } = {
      rounds: {},
      isLive: false
    };
    tournamentCutlines?.forEach(roundInfo => {
      if (roundInfo.roundId && roundInfo.cutLine) {
        dynamicCutLinesConfig.rounds[`Round${roundInfo.roundId}`] = roundInfo.cutLine;
      }
    });
    dynamicCutLinesConfig.isLive = !!isLive;
    return {
      dynamicCutLinesConfig
    };
  }, [leaderboardData, currentRoundId, tournament, dynRoundStatuses, isLive]);
  const getDrillDownData = useCallback(({
    rowData
  }: TableRowOptions<ElasticRoundAnalyticsWithAveragePoints>) => {
    const highestSeededTeamsBySchoolId = keyBy(orderBy(playerLeaderboardData?.results?.filter(player => player.teamLabel !== 'IND'), x => x.teamLabel, 'asc'), x => x.schoolId);
    const filteredData = playerLeaderboardData?.results?.filter(x => {
      if (x.schoolId === rowData.schoolId) {
        if (x.teamLabel === rowData.teamLabel) {
          return true;
        }
        if (highestSeededTeamsBySchoolId[rowData.schoolId]?.teamLabel === rowData.teamLabel) {
          return x.teamLabel === 'IND';
        }
      }
      return false;
    }) ?? [];
    return Promise.resolve(filteredData);
  }, [playerLeaderboardData]);
  const columns = useMemo(() => createStrokeplayColumns({
    type,
    displayMode,
    sortable: true,
    rounds: numRounds,
    rankingsEnabled,
    divisionName: tournament.division,
    isAdmin,
    winnerCount,
    winner,
    onWinnerSelect,
    allRoundsCompleted,
    courseInfoMap,
    isMultiCourse,
    playerRankingsDisplayCutoff,
    teamRankingsDisplayCutoff,
    currentRoundId,
    roundStatuses,
    isUpdatedAfterPublish,
    dynamicCutLinesConfig,
    cutLineTiesConfig,
    cutLineWinner,
    selectCutLineWinner,
    cutSetup
  }, mobile, leaderboardData?.results), [displayMode, rankingsEnabled, tournament, numRounds, mobile, type, isAdmin, winnerCount, winner, onWinnerSelect, courseInfoMap, isMultiCourse, playerRankingsDisplayCutoff, teamRankingsDisplayCutoff, currentRoundId, roundStatuses, isUpdatedAfterPublish]);
  return <ScorecardDialogProvider data={playerLeaderboardData?.results ?? []} format={tournament.eventType} courseInfoMap={courseInfoMap} playerRankingsDisplayCutoff={rankingsEnabled ? playerRankingsDisplayCutoff : 0} suppressProfileLinks={isAdmin}>
        <div className="flex flex-col">
          {!isAdmin && <div className="px-2 lg:px-4 py-2 bg-white flex justify-between items-center space-x-4">
              <div className="text-xs lg:text-sm text-secondary leading-tight">
                {rankingsEnabled && `* Divisional points displayed on ${type} screens`}
              </div>
              <div className="flex items-center space-x-4 p-2">
                {cutSetup && <div className="text-xs lg:text-sm text-secondary leading-tight flex items-center">
                    <div className="w-4 h-4 bg-coolgray-light mr-2" />
                    {`${cutStatus === 'final' ? '' : 'Projected'} Cut`}
                  </div>}
                <FilterSelector className="w-[180px]" value={displayMode} options={['score', 'stroke']} queryParamKey="displayMode" labelFormatter={value => `View: By ${value === 'score' ? 'Score' : 'Stroke'}`} />
              </div>
            </div>}
          <div>
            {leaderboardData ? <StrokeplayLeaderboardTable data={leaderboardData} currentRoundId={currentRoundId} tournament={tournament} columns={columns} courseInfoMap={courseInfoMap} isAdmin={!!isAdmin} isCut={isCut} isFirstCutRow={isFirstCutRow} isLiveScored={tournament.tournamentIntegrationId === 'scoreboardlive'} numRounds={numRounds} type={type} winnerCount={winnerCount} scoresCount={tournament.numTeamScoring} pollingInterval={pollingInterval} playerRankingsDisplayCutoff={rankingsEnabled ? playerRankingsDisplayCutoff : 0} getDrillDownData={getDrillDownData} dynamicCutLinesConfig={dynamicCutLinesConfig} tieResolverField={CUMULATIVE_DROP_SCORE_COLUMN_ID} cutSetup={cutSetup} /> : null}
          </div>
        </div>
      </ScorecardDialogProvider>;
});
type StrokeplayLeaderboardContextType = {
  tournament: ElasticTournament;
  courseInfoMap: Record<string, ElasticCourseData>;
  currentRoundId: string;
  numRounds: number;
  type: LeaderboardType;
  isAdmin: boolean;
  scoresCount?: number;
  playerRankingsDisplayCutoff?: number;
  cutSetup?: CutSetup;
};
const StrokeplayLeaderboardContext = createContext<StrokeplayLeaderboardContextType>({} as StrokeplayLeaderboardContextType);
const TeamExpandableComponent = memo(({
  data
}: TableRowExpandableComponentProps<ElasticRoundAnalyticsWithAveragePoints>) => {
  const {
    courseInfoMap,
    isAdmin,
    numRounds,
    playerRankingsDisplayCutoff,
    scoresCount,
    tournament,
    currentRoundId,
    cutSetup
  } = useContext(StrokeplayLeaderboardContext);
  const [roundId, setRoundId] = useState(currentRoundId);
  const roundIndex = parseInt(roundId, 10) - 1;
  const sortedData = useMemo(() => orderBy(data, [x => x.teamLabel === 'IND' ? 1 : 0, x => x.scores[roundIndex], x => x.holeThrough], ['asc', 'asc', 'desc']), [data, roundIndex]);
  useEffect(() => {
    setRoundId(currentRoundId);
  }, [currentRoundId]);
  return <ScorecardDialogProvider data={sortedData} format={tournament.eventType} courseInfoMap={courseInfoMap} playerRankingsDisplayCutoff={playerRankingsDisplayCutoff} suppressProfileLinks={isAdmin}>
        <HoleScorecard data={sortedData} eventType="Stroke Play" mode="score" roundId={roundId} courseInfoMap={courseInfoMap} numRounds={numRounds} scoresCount={scoresCount} isAdmin={isAdmin} tournament={tournament} onRoundIdChange={setRoundId} cutSetup={cutSetup} />
      </ScorecardDialogProvider>;
});
export type StrokeplayLeaderboardTableProps = {
  data: ElasticLeaderboardWithAveragePoints;
  currentRoundId: string;
  tournament: ElasticTournament;
  courseInfoMap: Record<string, ElasticCourseData>;
  numRounds: number;
  columns: TableColumn<ElasticRoundAnalyticsWithAveragePoints>[];
  type: LeaderboardType;
  isAdmin: boolean;
  isCut: (rowData: ElasticRoundAnalyticsWithAveragePoints) => boolean;
  isFirstCutRow: (rowData: ElasticRoundAnalyticsWithAveragePoints) => boolean;
  getDrillDownData: (options: TableRowOptions<ElasticRoundAnalyticsWithAveragePoints>) => Promise<ElasticRoundAnalyticsWithAveragePoints[]>;
  isLiveScored: boolean;
  scoresCount?: number;
  winnerCount?: number;
  pollingInterval: number;
  playerRankingsDisplayCutoff?: number;
  dynamicCutLinesConfig?: {
    rounds?: Record<string, number>;
    isLive?: boolean;
  };
  tieResolverField?: string;
  cutSetup?: CutSetup;
};
const StrokeplayLeaderboardTable = memo(({
  data,
  currentRoundId,
  courseInfoMap,
  numRounds,
  columns,
  type,
  isAdmin,
  isLiveScored,
  getDrillDownData,
  scoresCount,
  winnerCount,
  pollingInterval,
  playerRankingsDisplayCutoff,
  tournament,
  dynamicCutLinesConfig,
  tieResolverField,
  cutSetup
}: StrokeplayLeaderboardTableProps) => {
  const {
    showPlayerScorecard
  } = useScorecardDialogContext();
  const tablePropsByLeaderboardType = useMemo<Record<LeaderboardType, Partial<TableProps<ElasticRoundAnalyticsWithAveragePoints>>>>(() => ({
    player: {},
    team: {
      expandableComponent: TeamExpandableComponent
    }
  }), [currentRoundId, isAdmin, playerRankingsDisplayCutoff, courseInfoMap, scoresCount, numRounds]);
  const currentRoundIdAsNumber = parseInt(currentRoundId, 10);
  const rowCutLineConfig = useMemo(() => {
    const currentRoundCut = dynamicCutLinesConfig && dynamicCutLinesConfig.rounds && Number(dynamicCutLinesConfig.rounds[`Round${currentRoundIdAsNumber}`]) - 1;
    const cutScore = data.results?.at(currentRoundCut ?? 0)?.totalScore ?? 0;
    return currentRoundCut ? {
      afterIndex: currentRoundCut,
      content: <CUT_CONTENT cutScore={cutScore} isLive={dynamicCutLinesConfig?.isLive} />
    } : undefined;
  }, [dynamicCutLinesConfig, data.results, currentRoundIdAsNumber]);
  return <StrokeplayLeaderboardContext.Provider value={{
    currentRoundId,
    courseInfoMap,
    isAdmin,
    numRounds,
    tournament,
    type,
    playerRankingsDisplayCutoff,
    scoresCount,
    cutSetup
  }}>
        <Table<ElasticRoundAnalyticsWithAveragePoints> {...tablePropsByLeaderboardType[type]} data={data.results} columns={columns} headerClassName="thead-primary" stickyHeader stickyTopClassName="top-main-with-tab-nav-mobile lg:top-main-with-tab-nav" allowHorizontalScroll emptyMessage={<NoTournamentData isLiveScored={isLiveScored} />} expandable={type === 'team'} expandableRow={({
      rowData
    }) => !!rowData?.scores?.length} expandableData={type === 'team' ? getDrillDownData : undefined} hideFillerOnSort={true} fillerRowContents={rowCutLineConfig} secondarySortColumn={tieResolverField} expandableDataRemoteOptions={{
      pollingInterval
    }} rowClassName={({
      rowIndex,
      rowData
    }) => classNames('transition-colors duration-200 ease-out cursor-pointer', {
      'bg-gray-50': rowIndex % 2 === 1,
      'bg-white': rowIndex % 2 === 0,
      'hover:bg-gray-200': rowIndex % 2 === 1,
      'hover:bg-gray-100': rowIndex % 2 === 0
    }, winnerCount && winnerCount > 1 && rowIndex == winnerCount - 1 ? 'border-b-2 border-black' : '')} rowKey={({
      rowData
    }) => rowData.playerId ?? `${rowData.schoolId}-${rowData.teamLabel ?? ''}`} onRowClick={({
      rowData
    }) => {
      if (type === 'player') {
        showPlayerScorecard(rowData.playerId!);
      }
    }} />
      </StrokeplayLeaderboardContext.Provider>;
});
const CUT_CONTENT: FC<{
  cutScore: number;
  isLive?: boolean;
}> = ({
  cutScore,
  isLive
}) => <div className="bg-gray-200 text-center text-sm flex items-center justify-center py-3 font-bold space-x-2" data-sentry-component="CUT_CONTENT" data-sentry-source-file="strokeplay-leaderboard.tsx">
    <Info size={16} data-sentry-element="Info" data-sentry-source-file="strokeplay-leaderboard.tsx" />
    <span>
      {isLive ? 'Projected cut line' : 'The following teams did not make the cut'}{' '}
      {cutScore > 0 ? '+' : ''}
      {cutScore} (Ties will be resolved)
    </span>
  </div>;